/**
 * Hosted-mode predicates and state derivations for the Roblox platform private
 * alpha. Kept free of React so both the command palette boundary and the
 * project surface can share one definition of "this browser is talking to the
 * hosted Worker, not a local T3 environment".
 */

import type { HostedApiErrorCode } from "./api";

export type HostedAuthGateStatus = string;

/**
 * What every hosted predicate needs: where the app is served from, and whether
 * a Clerk provider is mounted for this build (`clerkProviderEnabled`). Hosted
 * surfaces talk to the Worker through the Clerk session, so a hosted bundle
 * without a provider must not mount them: their hooks would run outside the
 * provider and throw.
 */
export interface HostedAuthInput {
  /** `authGateState.status` from the root route context. */
  readonly authGateStatus: HostedAuthGateStatus;
  readonly clerkProviderMounted: boolean;
}

export interface HostedModeInput extends HostedAuthInput {
  /** Number of local/remote T3 environments the catalog knows about. */
  readonly environmentCount: number;
}

/**
 * True when the app is served by the hosted Worker. Hosted mode can still have
 * a linked local environment; that is what `hostedProjectsOnly` distinguishes.
 */
export function isHostedMode(input: HostedAuthInput): boolean {
  return input.authGateStatus === "hosted-static";
}

/**
 * The hosted Worker's `/api` routes authenticate with the Clerk session, so a
 * hosted bundle with no provider cannot reach any of them. Anything that calls
 * a hosted endpoint or a Clerk hook gates on this, which is also exactly the
 * condition under which a Clerk provider is mounted above it.
 */
export function isHostedApiAvailable(input: HostedAuthInput): boolean {
  return isHostedMode(input) && input.clerkProviderMounted;
}

/**
 * True when hosted mode is active and no local T3 environment is connected, so
 * filesystem-backed flows (environment browsing, folder pickers) cannot work
 * and hosted project records are the only projects available.
 */
export function isHostedProjectsOnly(input: HostedModeInput): boolean {
  return isHostedMode(input) && input.environmentCount === 0;
}

/**
 * The mount condition for the hosted project surfaces that replace T3's local
 * project UI. Without a provider the hosted surfaces cannot authenticate
 * anything, so the app falls back to its normal environment-connection flow
 * instead of rendering controls that can only fail.
 */
export function shouldMountHostedSurfaces(input: HostedModeInput): boolean {
  return isHostedProjectsOnly(input) && input.clerkProviderMounted;
}

export type AddProjectRoute = "hosted-name-dialog" | "environment-browse";

/**
 * The single decision the command palette needs: hosted browsers with no local
 * environment get a name-only dialog, everything else keeps T3's existing
 * environment/source browsing.
 */
export function resolveAddProjectRoute(input: HostedModeInput): AddProjectRoute {
  return shouldMountHostedSurfaces(input) ? "hosted-name-dialog" : "environment-browse";
}

/**
 * Settings sections that exist only on the hosted deployment.
 *
 * Memory is stored by the Worker and edited through its authenticated `/api`
 * routes, so on local and desktop T3 the section has nothing behind it: its
 * panel would call Clerk hooks with no provider and request endpoints that do
 * not exist. Filtering the nav and redirecting the route keeps the local
 * settings list free of a dead entry rather than showing one that errors.
 */
export const HOSTED_ONLY_SETTINGS_PATHS: ReadonlySet<string> = new Set(["/settings/memory"]);

export function isHostedOnlySettingsPath(path: string): boolean {
  return HOSTED_ONLY_SETTINGS_PATHS.has(path);
}

export function isSettingsSectionVisible(input: HostedAuthInput & { readonly path: string }) {
  return isHostedOnlySettingsPath(input.path) ? isHostedApiAvailable(input) : true;
}

export function visibleSettingsNavItems<Item extends { readonly to: string }>(
  items: ReadonlyArray<Item>,
  input: HostedAuthInput,
): ReadonlyArray<Item> {
  return items.filter((item) => isSettingsSectionVisible({ ...input, path: item.to }));
}

/**
 * The route guard half of the same rule. A hidden nav entry is not enough: a
 * bookmarked or typed `/settings/memory` would still mount a panel whose Clerk
 * hooks have no provider, so the route redirects instead of rendering.
 */
export function settingsSectionRedirect(
  input: HostedAuthInput & { readonly path: string },
): "/settings/general" | null {
  return isSettingsSectionVisible(input) ? null : "/settings/general";
}

export type HostedOwnerGateState =
  | { readonly kind: "clerk-unconfigured" }
  | { readonly kind: "loading" }
  | { readonly kind: "signed-out" }
  | { readonly kind: "not-owner"; readonly message: string }
  | { readonly kind: "allowed" };

export interface HostedOwnerGateInput {
  /** Whether a Clerk publishable key and JWT template are configured. */
  readonly clerkConfigured: boolean;
  readonly clerkLoaded: boolean;
  readonly signedIn: boolean;
  /** Latest hosted API error code observed for this session, if any. */
  readonly apiErrorCode: HostedApiErrorCode | null;
  readonly apiErrorMessage?: string | null;
}

export const HOSTED_PRIVATE_ALPHA_MESSAGE =
  "This deployment is a private alpha. Your account is signed in but not on the allow list yet.";

/**
 * Owner enforcement lives in the Worker; the client only reflects it. A 403
 * from any hosted endpoint is the signal that a signed-in account is not the
 * owner, so no user id ever has to ship in the bundle.
 */
export function resolveHostedOwnerGateState(input: HostedOwnerGateInput): HostedOwnerGateState {
  if (!input.clerkConfigured) {
    return { kind: "clerk-unconfigured" };
  }
  if (!input.clerkLoaded) {
    return { kind: "loading" };
  }
  if (!input.signedIn) {
    return { kind: "signed-out" };
  }
  if (input.apiErrorCode === "owner_only") {
    return {
      kind: "not-owner",
      message: input.apiErrorMessage?.trim() || HOSTED_PRIVATE_ALPHA_MESSAGE,
    };
  }
  if (input.apiErrorCode === "unauthenticated") {
    return { kind: "signed-out" };
  }
  return { kind: "allowed" };
}

export interface HostedProjectNameValidation {
  readonly value: string;
  readonly error: string | null;
}

/**
 * Matches the Worker's `validateProjectName` cap exactly. A client cap below the
 * server's would silently truncate a name the backend would have accepted, and
 * one above it would let a request through only to be rejected.
 */
export const HOSTED_PROJECT_NAME_MAX_LENGTH = 80;

/**
 * Name-only creation still needs a real check: an empty submit must not reach
 * the Worker, and the field is bounded to the same limit the Worker enforces.
 *
 * Length is counted in code points rather than UTF-16 units, because the Worker
 * counts that way too; a name of 80 emoji is one character over on one side and
 * eighty on the other unless both agree.
 */
export function validateHostedProjectName(raw: string): HostedProjectNameValidation {
  const value = raw.trim().replace(/\s+/g, " ");
  if (value.length === 0) {
    return { value, error: "Enter a project name." };
  }
  if (Array.from(value).length > HOSTED_PROJECT_NAME_MAX_LENGTH) {
    return {
      value,
      error: `Use ${HOSTED_PROJECT_NAME_MAX_LENGTH} characters or fewer.`,
    };
  }
  return { value, error: null };
}
